import { useState, useEffect } from 'react';
import { X, Search, Crown, Users, BookOpen } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminVIPModal({ user, onClose }) {
  const { session } = useAuth(); // Get session for JWT token
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [toggling, setToggling] = useState(null); // Track which user is being toggled
  const [syncing, setSyncing] = useState(false); // Track sync operation
  const [syncResult, setSyncResult] = useState(null); // Store sync results

  useEffect(() => {
    // Check if we have a valid session token
    if (!session?.access_token) {
      console.error('❌ No session token available');
      alert('Authentication error: Please log out and log back in.');
      onClose();
      return;
    }
    
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      const url = `${API_URL}/api/admin/users`;
      
      // Use JWT authentication instead of deprecated X-User-Email header
      const headers = {
        'Authorization': `Bearer ${session?.access_token || ''}`
      };
      
      console.log('🔍 Fetching admin users...');
      console.log('🔍 URL:', url);
      console.log('🔍 Using JWT auth');
      
      const response = await fetch(url, { headers });
      
      console.log('🔍 Response status:', response.status);
      console.log('🔍 Response ok?:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response error:', errorText);
        throw new Error(`Failed to load users (HTTP ${response.status})`);
      }

      const data = await response.json();
      console.log('✅ Users fetched:', data);
      
      // Deduplicate users by email (keep the one with most games played)
      const uniqueUsers = [];
      const emailMap = new Map();
      
      (data.users || []).forEach(user => {
        const existing = emailMap.get(user.email);
        if (!existing || (user.total_games_played || 0) > (existing.total_games_played || 0)) {
          emailMap.set(user.email, user);
        }
      });
      
      emailMap.forEach(user => uniqueUsers.push(user));
      console.log(`📊 Deduped: ${data.users?.length || 0} → ${uniqueUsers.length} unique users`);
      
      setUsers(uniqueUsers);
    } catch (error) {
      console.error('❌ Error fetching users:', error);
      // Simple string-only alert to avoid cloning issues
      const errorMsg = error instanceof Error ? error.message : String(error);
      alert('Failed to load users: ' + errorMsg + '\n\nMake sure you have admin access.');
    } finally {
      setLoading(false);
    }
  };

  const toggleVIP = async (email, currentStatus) => {
    try {
      setToggling(email);
      
      const response = await fetch(`${API_URL}/api/admin/toggle-vip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`
        },
        body: JSON.stringify({
          email,
          is_vip: !currentStatus
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to toggle VIP (HTTP ${response.status})`);
      }

      const data = await response.json();
      console.log('✅ VIP status updated:', data);

      // Refresh user list
      await fetchUsers();
    } catch (error) {
      console.error('❌ Error toggling VIP:', error);
      const errorMsg = error instanceof Error ? error.message : String(error);
      alert('Failed to ' + (currentStatus ? 'revoke' : 'grant') + ' VIP status: ' + errorMsg);
    } finally {
      setToggling(null);
    }
  };

  const syncSupabaseUsers = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      
      console.log('🔄 Starting Supabase user sync...');
      
      const response = await fetch(`${API_URL}/api/admin/sync-supabase-users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token || ''}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Sync failed (HTTP ${response.status}): ${errorText}`);
      }

      const data = await response.json();
      console.log('✅ Sync complete:', data);
      
      setSyncResult(data);
      
      // Refresh user list to show newly synced users
      await fetchUsers();
      
    } catch (error) {
      console.error('❌ Error syncing users:', error);
      const errorMsg = error instanceof Error ? error.message : String(error);
      alert('Failed to sync users from Supabase: ' + errorMsg);
    } finally {
      setSyncing(false);
    }
  };

  // Filter users based on search query
  const filteredUsers = users.filter(u => 
    u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Stats
  const vipCount = users.filter(u => u.is_vip).length;
  const premiumCount = users.filter(u => u.is_premium).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border-2 border-yellow-500/30">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-600 to-orange-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crown className="w-6 h-6 text-yellow-200" />
            <div>
              <h2 className="text-2xl font-bold text-white">VIP Management</h2>
              <p className="text-sm text-yellow-100">Admin Panel - Early Beta Access</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Game Flow Reference (admin cheat sheet — starting values, turn order) */}
            <a
              href="/game-flow-reference.html"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-flow-reference-link"
              className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 border border-slate-500"
              title="Starting values, turn sequence, unlock schedule"
            >
              <BookOpen className="w-4 h-4" />
              Flow
            </a>
            
            {/* Game Mechanics Reference (admin cheat sheet) */}
            <a
              href="/game-mechanics-reference.html"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-mechanics-reference-link"
              className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 border border-slate-500"
              title="Open the full action probabilities & costs cheat sheet"
            >
              <BookOpen className="w-4 h-4" />
              Mechanics
            </a>
            
            {/* Sync Button */}
            <button
              onClick={syncSupabaseUsers}
              disabled={syncing}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2"
              title="Sync all Supabase users to MongoDB"
            >
              {syncing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Syncing...
                </>
              ) : (
                <>
                  <Users className="w-4 h-4" />
                  Sync Users
                </>
              )}
            </button>
            
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>

        {/* Sync Results Banner */}
        {syncResult && (
          <div className="bg-green-500/20 border-b-2 border-green-500 px-6 py-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-green-400 font-bold text-lg mb-2 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  ✅ Sync Complete!
                </h3>
                <div className="grid grid-cols-4 gap-3 text-sm">
                  <div className="bg-slate-800/50 p-2 rounded text-center">
                    <div className="text-slate-400 text-xs">Total</div>
                    <div className="text-white font-bold text-lg">{syncResult.summary?.total_supabase_users || 0}</div>
                  </div>
                  <div className="bg-green-900/30 p-2 rounded text-center">
                    <div className="text-green-400 text-xs">Created</div>
                    <div className="text-green-300 font-bold text-lg">{syncResult.summary?.created_in_mongodb || 0}</div>
                  </div>
                  <div className="bg-blue-900/30 p-2 rounded text-center">
                    <div className="text-blue-400 text-xs">Existed</div>
                    <div className="text-blue-300 font-bold text-lg">{syncResult.summary?.already_existed || 0}</div>
                  </div>
                  <div className="bg-red-900/30 p-2 rounded text-center">
                    <div className="text-red-400 text-xs">Failed</div>
                    <div className="text-red-300 font-bold text-lg">{syncResult.summary?.failed || 0}</div>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setSyncResult(null)}
                className="text-green-300 hover:text-green-200 ml-4"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Stats Bar */}
        <div className="bg-gray-800/50 px-6 py-3 border-b border-gray-700 grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-sm text-gray-400">Total Users</div>
            <div className="text-xl font-bold text-white">{users.length}</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-gray-400">VIP</div>
            <div className="text-xl font-bold text-yellow-400">{vipCount}</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-gray-400">Premium</div>
            <div className="text-xl font-bold text-green-400">{premiumCount}</div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-6 py-4 bg-gray-800/30">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 transition-colors"
            />
          </div>
        </div>

        {/* User List */}
        <div className="overflow-y-auto max-h-[500px] px-6 py-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-yellow-500 border-t-transparent"></div>
              <p className="text-gray-400 mt-4">Loading users...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No users found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredUsers.map((u) => (
                <div
                  key={u.email}
                  className="bg-gray-800/50 rounded-lg p-4 border border-gray-700 hover:border-gray-600 transition-all"
                >
                  <div className="flex items-center justify-between gap-4">
                    
                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="font-medium text-white truncate">{u.email}</div>
                        {u.is_premium && (
                          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full border border-green-500/30">
                            Premium
                          </span>
                        )}
                        {u.is_vip && (
                          <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full border border-yellow-500/30 flex items-center gap-1">
                            <Crown className="w-3 h-3" />
                            VIP
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-x-3 gap-y-1 text-xs text-gray-400 flex-wrap">
                        <span title="Games started (from games collection)">
                          🎮 <b className="text-gray-200">{u.games_started || 0}</b> games
                        </span>
                        <span title="Sum of turns reached across all their games — engagement signal">
                          🎯 <b className="text-gray-200">{u.total_turns_played || 0}</b> turns played
                        </span>
                        <span title="Furthest turn reached in any single game">
                          🏔️ farthest <b className="text-gray-200">T{u.furthest_turn || 0}</b>
                        </span>
                        <span title="Total playtime across all games (first-created to last-updated)">
                          ⏱️ {(() => {
                            const ms = u.total_playtime_ms || 0;
                            if (ms < 60000) return '<1m';
                            const min = Math.floor(ms / 60000);
                            if (min < 60) return `${min}m`;
                            const hr = Math.floor(min / 60);
                            return `${hr}h ${min % 60}m`;
                          })()}
                        </span>
                        <span title="Games completed (legacy counter — skipped for VIP/Premium)">
                          ✅ {u.total_games_played || 0} finished
                        </span>
                        <span>· {u.games_in_current_period || 0}/2 period</span>
                        {u.last_played_at && (
                          <span title="Last time they played">
                            · last {new Date(u.last_played_at).toLocaleDateString()}
                          </span>
                        )}
                        {u.created_at && (
                          <span>· joined {new Date(u.created_at).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>

                    {/* VIP Toggle Button */}
                    <button
                      onClick={() => toggleVIP(u.email, u.is_vip)}
                      disabled={toggling === u.email || u.is_premium}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                        u.is_vip
                          ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 hover:bg-yellow-500/30'
                          : 'bg-gray-700 text-gray-300 border border-gray-600 hover:bg-yellow-500/20 hover:text-yellow-400 hover:border-yellow-500/30'
                      }`}
                      title={u.is_premium ? 'User already has Premium' : (u.is_vip ? 'Click to revoke VIP' : 'Click to grant VIP')}
                    >
                      {toggling === u.email ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                          ...
                        </span>
                      ) : u.is_vip ? (
                        <span className="flex items-center gap-1">
                          <Crown className="w-4 h-4" />
                          Revoke VIP
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <Crown className="w-4 h-4" />
                          Grant VIP
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-800/50 px-6 py-3 border-t border-gray-700 flex items-center justify-between">
          <div className="text-sm text-gray-400">
            <span className="font-medium text-yellow-400">VIP</span> = Unlimited games (same as Premium)
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
